import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
	RolesPermissionsComponent,
	RolesPermissionsModule
} from '../roles-permissions';
import { AuthComponent } from '../auth';
import { AuthModule } from '../auth/auth.module';
import { CommonErrorComponent, CommonErrorModule } from '../common-error';
import { RegistrationComponent, RegistrationModule } from '../registration';
import { ShellComponent } from '../shell/shell.component';
import { ShellModule } from '../shell/shell.module';
import { MsalGuard } from '@azure/msal-angular';
import { ConfirmRegistrationComponent } from '../registration/sub-modules/confirm-registration';
import { AccessGuard } from '../../guards/access.guard.service';
import { PendingChangesGuard } from '../../guards/pending-changes.guard.service';
import { PermissionGuard } from '../../guards/permission/permission.guard.service';
import { PermissionCategory, PermissionDefault } from '../../services/shared/shared-enums';
import { LocationAccessGuard } from '../../guards/location-access.guard.service';
import { AuthGuard } from '../../guards/auth/auth.guard';
import { MainEntryComponent } from '../main-entry';
import { LoginGuard } from '../../guards/login.guard.service';
import { LandingPageGuard } from '../../guards/landing-page.guard.service';
import { CustomerPortalComponent, CustomerPortalModule } from '../customer-portal';
import { CustomerSupportComponent } from '../customer-support/customer-support.component';
import { CustomerSupportModule } from '../customer-support/customer-support.module';
import { ContactUsComponent } from '../contact-us/contact-us.component';
import { ContactUsModule } from '../contact-us/contact-us.module';
import { PrivacyAndTermsAndConditionsComponent } from '../../raymond/components/raymondContentPages/privacy-and-terms-and-conditions.component';
import { PrivacyAndTermsAndConditionsModule } from '../../raymond/components/raymondContentPages/privacy-and-terms-and-conditions.module';
import { environment } from '../../../environments/environment';

const routes: Routes = [
	{
		path: 'error',
		component: CommonErrorComponent
	},
	{
		path: 'auth',
		component: AuthComponent,
		canActivate: [MsalGuard]
	},
	{
		path: 'registration',
		canActivate: [AuthGuard],
		component: RegistrationComponent,
		data: {
			title: 'Registration'
		}
	},
	{
		path: 'confirmation',
		canActivate: [AuthGuard],
		component: ConfirmRegistrationComponent
	},
	{
		path: 'pending-approval',
		canActivate: [AuthGuard],
		component: ConfirmRegistrationComponent
	},
	{
		path: '',
		component: MainEntryComponent,
		canActivate: [LoginGuard],
		data: {
			title: 'MyToyota Customer Portal | Toyota Material Handling'
		}
	},
	{
		path: 'contact-us',
		component: ContactUsComponent,
		data: {
			title: 'contact us'
		}
	},
	{
		path: 'content-page/:type',
		component: PrivacyAndTermsAndConditionsComponent,
		data: {
			title: 'privacy terms and conditions'
		}
	},
	{
		path: 'app',
		component: ShellComponent,
		canActivate: [LandingPageGuard],
		children: [
			{
				path: '',
				redirectTo: 'dashboard',
				pathMatch: 'full'
			},
			{
				path: 'customer-support',
				component: CustomerSupportComponent,
				canActivate: [AccessGuard],
				data: {
					title: 'Customer Support'
				}
			},
			{
				path: 'customerportal',
				canActivate: [AccessGuard],
				component: CustomerPortalComponent,
				data: {
					title: 'CustomerPortal'
				}
			},
			{
				path: 'dashboard',
			    canActivate: [ AccessGuard, PermissionGuard, LocationAccessGuard ],
				data: {
					title: 'MyDashboard',
					permissions: { category: PermissionCategory.dashBoard, action: PermissionDefault.viewDashboard }
				},
			 loadChildren: () =>
					import('../dashboard/dashboard.module').then((m) => m.DashboardModule)
		    },
			{ path: 'error', component: CommonErrorComponent },
			{
				path: 'roles-permissions',
				canActivate: [ AccessGuard, PermissionGuard ],
				canDeactivate: [PendingChangesGuard],
				component: RolesPermissionsComponent,
				data: {
					title: 'Roles & Permissions',
					permissions: { category: PermissionCategory.rolesPermissions, action: PermissionDefault.viewDetails }
				}
			},
			{
				path: 'myfleet',
				canActivate: [ AccessGuard, PermissionGuard, LocationAccessGuard ],
				data: {
					title: 'MyFleet',
					permissions: { category: PermissionCategory.myFleet, action: PermissionDefault.viewMyFleetDetails },
					breadcrumb: 'MyFleet'
				},
				loadChildren: () =>
					import('../myfleet/myfleet.module').then((module) => module.MyfleetModule)
			},
			{
				path: 'myquotes',
				canActivate: [ AccessGuard, PermissionGuard ],
				data: {
					title: 'MyQuotes',
					permissions: { category: PermissionCategory.myQuote, action: PermissionDefault.viewMyQuote }
				},
				loadChildren: () =>
					import('../my-quotes/my-quotes.module').then((module) => module.MyQuotesModule)
			},
			{
				path: 'my-insights',
				canActivate: [ AccessGuard, PermissionGuard ],
				data: {
					title: 'MyInsights',
					permissions: { category: PermissionCategory.myInsights, action: PermissionDefault.viewMyInsights },
					breadcrumb: 'Connected Solutions'
				},
				loadChildren: () =>
					import('../my-insights/my-insights.module').then((module) => module.MyInsightsModule)
			},
			{ path: 'service-management',
				canActivate: [ AccessGuard, PermissionGuard, LocationAccessGuard ],
				data: {
					title: 'MyService',
					breadcrumb: environment.isRaymond ? 'Service Management' : 'Service and Repairs',
					permissions: { category: PermissionCategory.serviceManagement, action: PermissionDefault.viewServiceManagement }
				},
				loadChildren: () =>
					import('../service-management/components/service-management-page/service-management.module').then((module) => module.ServiceManagementModule) },
			{
				path: 'user-management',
				canActivate: [ AccessGuard, PermissionGuard, LocationAccessGuard ],
				data: { title: 'User Management',
					breadcrumb: 'User Management',
					permissions: { category: PermissionCategory.userManagement, action: PermissionDefault.viewUserManagement }
				},
				loadChildren: () =>
					import('../user-management/user-management.module').then((module) => module.UserManagementModule) },
			{
				path: 'notification',
				canActivate: [ AccessGuard, LocationAccessGuard, PermissionGuard ],
				data: { title: 'Notifications',
					permissions: { category: PermissionCategory.notification, action: PermissionDefault.viewNotification }
				},
				loadChildren: () =>
					import('../notifications/notification.module').then((module) => module.NotificationModule)
			},
			{
				path: 'order-management',
				canActivate: [AccessGuard],
				data: {
					title: 'MyOrders',
				    breadcrumb: 'Orders'
				},
				loadChildren: () =>
					import('../order-management/order-management.module').then((m) => m.OrderManagementModule)
			},
			{
				path: 'account-settings',
				canActivate: [AccessGuard],
				data: { title: 'Account Settings', breadcrumb: 'Account Settings' },
				loadChildren: () =>
					import('../account-settings/account-settings.module').then((m) => m.AccountSettingsModule)
			},
			{
				path: 'location-management',
				canActivate: [AccessGuard],
				data: {
					permissions: { category: PermissionCategory.locationManagement, action: PermissionDefault.viewLocationManagement },
					title: 'Location Management',
					breadcrumb: 'Location Management'
				},
				loadChildren: () =>
					import('../location-management/location-management.module').then((m) => m.LocationManagementModule)
			},
			{
				path: 'analytics-reporting',
				canActivate: [AccessGuard],
				data: {
					title: 'Analytics/Reporting'
				},
				loadChildren: () =>
					import('../../raymond/components/analytics-reporting/analytics-reporting.module').then((m) => m.AnalyticsReportingModule)
			},
			{
				path: 'manage-dashboard',
				data: {
					title: 'Manage Dashboard'
				},
				loadChildren: () =>
					import('../../raymond/components/manage-dashboard/manage-dashboard.module').then((m) => m.ManageDashboardModule)
			},
			{ path: '**', redirectTo: 'error' }
		]
	},
	{ path: '**', redirectTo: 'error', pathMatch: 'full' }
];
@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			anchorScrolling: 'enabled'
		}),
		RolesPermissionsModule,
		CommonErrorModule,
		RegistrationModule,
		CustomerPortalModule,
		AuthModule,
		CustomerSupportModule,
		ContactUsModule,
		PrivacyAndTermsAndConditionsModule,
		ShellModule.forRoot()
	],
	providers: [MsalGuard],
	exports: [RouterModule]
})
export class AppRoutingModule { }
